export const en = {
  title: 'Run video matching flow',
  back: 'Back to conversations',
  loading: 'Loading the run video matching flow',
  turns: 'Completed turns',
  matchedTurns: 'Matched videos',
  calls: 'Related calls',
  startedAt: 'Run started',
  runStatus: { active: 'Active', completed: 'Completed' },
  empty: {
    title: 'No completed turns',
    description: 'Complete a player turn to generate a video matching flow.',
  },
  zoomOut: 'Zoom out',
  zoomIn: 'Zoom in',
  fitView: 'Fit',
  turnNumber: 'Turn {number}',
  playerInput: 'Player input',
  candidateCount: '{count} candidates',
  candidateRank: 'Rank {rank}',
  meetsThreshold: 'At or above threshold {threshold}',
  belowThreshold: 'Below threshold {threshold}',
  noRenderedVideo: 'This resource is a storyboard and has no rendered video yet.',
  openResource: 'Open resource',
  openResourceEmbedding: 'Open resource Embedding log',
  openTaskLogs: 'Open generation task logs',
  noLinkedTask: 'Legacy resource without a linked generation task',
  noNodeExternalCall: 'This step did not call an external model or service. Its query and decision are recorded under Execution details.',
  inspector: 'Node details',
  details: 'Details',
  resultMethod: 'Final method',
  traceCapture: 'Trace record',
  relatedConversations: 'Related conversations',
  embeddingInput: 'Embedding model input',
  embeddingOutput: 'Embedding call output',
  providerModel: 'Provider and model',
  vectorStorageNote: 'The raw vector is not persisted. Its dimensions and call identifiers are shown here; the following vector-search node records the actual candidate results and similarities.',
  agentCalls: 'Conversation model input and output',
  agentCallsDescription: 'The complete request context and model response for each Agent call in this turn.',
  loadingAgentCalls: 'Loading model input and output',
  noAgentCalls: 'No Agent model calls are associated with this turn.',
  openModelInputOutput: 'View model input and output',
  finalDecisionReason: 'Final decision evidence',
  modelDecisionReason: 'Model decision reason',
  stepRecordedReason: 'Recorded step reason',
  resourceStatus: {
    draft: 'Draft',
    in_review: 'In review',
    published: 'Published',
    rejected: 'Rejected',
    archived: 'Archived',
  },
  execution: {
    title: 'What this step did',
    noExternalCall: 'No external call; executed against local configuration or database data',
    activeBinding: 'Active binding only',
    semanticInputSource: 'Scene transition semantic text',
    playerInputSource: 'Player input, unchanged',
    similarityDescending: 'Cosine similarity, highest first',
    semanticDecisionNext: 'The recalled candidates are passed to the model decision step; vector rank alone does not select the video',
    localVectorSearch: 'Local vector-index query; it has no separate provider call log',
    llmDecision: 'LLM judges event consistency, spoiler constraints, and stage continuity',
    intentLlmDecision: 'LLM selects the candidate that matches the player intent, or rejects all candidates',
    topOneThresholdNoLlm: 'Select rank 1 when it reaches the direct-match threshold; no LLM is called',
    intentSelectionNext: 'Rank 1 is below the direct-match threshold; pass the recalled candidates to the LLM',
    belowMinimumNoLlm: 'All candidates are below the minimum candidate threshold; no LLM is called',
    noCandidateDecision: 'No candidate was returned, so no selection was attempted',
    playableStoryboards: 'In-review or published resources; unrendered storyboards are included by the current runtime setting',
    playableVideos: 'In-review or published resources that already have a rendered video',
    intentStoryboards: 'Same scenario pack and intent-video template; unrendered storyboards are included by the current runtime setting',
    intentVideos: 'Same scenario pack and intent-video template; rendered videos only',
    exactResourceStore: 'Video resource store',
    semanticBindingStore: 'Semantic reuse bindings',
    sceneVectorIndex: 'Scene video vector index',
    intentVectorIndex: 'Intent video vector index',
    queryCount: '{count} rows',
    bucketAvailable: 'Matching bucket exists',
    facts: {
      queryTarget: 'Query target',
      queryResult: 'Query result',
      nextStep: 'Next step',
      switchResult: 'Switch result',
      settingResult: 'Player setting',
      scenarioVersion: 'Scenario version',
      transitionHash: 'Exact transition fingerprint',
      resourceScope: 'Candidate scope',
      externalCall: 'Call type',
      bindingStatus: 'Binding status',
      inputSource: 'Input source',
      rankingRule: 'Ranking rule',
      nextDecision: 'Next decision',
      decisionMethod: 'Selection rule',
      candidateLimit: 'Recall limit',
      minimumSimilarityThreshold: 'Minimum candidate threshold',
      directMatchThreshold: 'Direct-match threshold',
      eligibleCandidateCount: 'Candidates sent to model',
      selectedResource: 'Selected resource',
      finalSimilarity: 'Final similarity',
    },
    summary: {
      cgRecallGate: {
        passed: 'The server-wide video retrieval switch was enabled, so matching continued.',
        stopped: 'The server-wide video retrieval switch was disabled, so the remaining matching steps were skipped.',
      },
      smartMatchSetting: {
        passed: 'The player enabled video performance, so action video matching was allowed.',
        stopped: 'The player disabled video performance, so action video matching was skipped.',
      },
      exactMatch: {
        selected: 'Found a playable resource with the same scenario version and exact transition fingerprint; it was selected immediately.',
        miss: 'Queried playable resources by scenario version and exact transition fingerprint; no row matched.',
      },
      savedSemanticMatch: {
        selected: 'Found a previously approved active semantic binding for this transition and reused its selected resource.',
        miss: 'Queried active semantic bindings by scenario version and transition fingerprint; no binding matched.',
      },
      semanticCandidateBucket: {
        available: 'The local index contained resources in the same location, action mode, intent, template, and embedding-model bucket.',
        empty: 'The local index had no resource in the same location, action mode, intent, template, and embedding-model bucket, so scene embedding and model decision were skipped.',
      },
      semanticEmbedding: {
        ok: 'Sent the scene transition semantic text to the Embedding model and received a query vector.',
        error: 'The scene semantic Embedding call failed, so scene-vector retrieval could not continue.',
      },
      semanticVectorSearch: {
        ok: 'The local vector index returned {count} scene candidates. These candidates still require the following model decision.',
        empty: 'The local vector index returned no scene candidate, so the model decision was skipped.',
      },
      semanticDecision: {
        selected: 'The LLM accepted one recalled candidate and selected it.',
        rejected: 'The LLM rejected every recalled candidate after checking the scene constraints.',
        error: 'The model decision call failed, so no scene candidate was selected.',
      },
      intentSelection: {
        selected: 'The LLM selected one candidate after comparing it with the player input.',
        rejected: 'The LLM rejected every recalled intent candidate.',
        error: 'The model video-selection call failed, so no candidate was selected.',
      },
      intentEmbedding: {
        ok: 'Sent the player input unchanged to the Embedding model and received an intent query vector.',
        error: 'The player-input Embedding call failed, so intent-vector retrieval could not continue.',
      },
      intentVectorSearch: {
        selected: 'Read up to {limit} candidates in descending similarity. Rank 1 scored {similarity} ≥ {threshold}, so resource {resource} was selected directly. No LLM was called.',
        needs_llm_selection: 'Read up to {limit} candidates in descending similarity. Rank 1 scored {similarity}, between the minimum threshold {minimum} and direct-match threshold {direct}; {eligible} eligible candidates continue to model selection.',
        below_minimum: 'Read up to {limit} candidates in descending similarity. Rank 1 scored {similarity} < the minimum threshold {minimum}; no candidate was sent to the model.',
        empty: 'Read up to {limit} candidates from the intent index, but no eligible resource was returned. No LLM was called.',
      },
      result: {
        selected: 'The selected resource and its matching evidence were written as the final result for this turn.',
        uncovered: 'All executed matching levels ended without a selected resource; this turn remained uncovered.',
      },
    },
  },
  openRun: 'View full run flow',
  openFocusedRun: 'Locate in full run flow',
  filters: {
    mode: 'Interaction mode',
    intent: 'Normalized intent',
    versionId: 'Scenario version',
    locationId: 'Location',
    embeddingModelId: 'Embedding model',
    semanticTemplateVersion: 'Semantic template',
  },
}

export const zh = {
  title: '单局视频匹配完整链路',
  back: '返回对话列表',
  loading: '正在加载单局视频匹配链路',
  turns: '已完成回合',
  matchedTurns: '匹配到视频',
  calls: '关联调用',
  startedAt: '开始时间',
  runStatus: { active: '进行中', completed: '已完成' },
  empty: {
    title: '这局还没有已完成回合',
    description: '完成一次玩家输入后，这里会生成视频匹配链路。',
  },
  zoomOut: '缩小',
  zoomIn: '放大',
  fitView: '全览',
  turnNumber: '第 {number} 回合',
  playerInput: '玩家原始输入',
  candidateCount: '{count} 条候选',
  candidateRank: '第 {rank} 名',
  meetsThreshold: '达到阈值 {threshold}',
  belowThreshold: '低于阈值 {threshold}',
  noRenderedVideo: '尚未生成视频，仅有分镜。',
  openResource: '查看资源详情',
  openResourceEmbedding: '查看资源向量日志',
  openTaskLogs: '查看出片任务日志',
  noLinkedTask: '历史资源，无关联生成任务',
  noNodeExternalCall: '本节点无外部调用，查询条件与判定结果见执行详情。',
  inspector: '节点详情',
  details: '执行详情',
  resultMethod: '最终匹配方式',
  traceCapture: '链路记录',
  relatedConversations: '关联对话',
  embeddingInput: '向量模型输入',
  embeddingOutput: '向量调用结果',
  providerModel: '厂商与模型',
  vectorStorageNote: '系统不持久化原始向量正文；这里展示维度和调用标识，下一步“向量检索”节点展示实际召回候选及相似度。',
  agentCalls: '对话模型输入与输出',
  agentCallsDescription: '展示本回合每次 Agent 调用的完整请求上下文和模型响应。',
  loadingAgentCalls: '正在加载模型输入与输出',
  noAgentCalls: '本回合没有关联的 Agent 模型调用。',
  openModelInputOutput: '查看模型输入输出',
  finalDecisionReason: '最终判定依据',
  modelDecisionReason: '模型判定理由',
  stepRecordedReason: '步骤记录原因',
  resourceStatus: {
    draft: '草稿',
    in_review: '待审核',
    published: '已发布',
    rejected: '已驳回',
    archived: '已归档',
  },
  execution: {
    title: '执行说明',
    noExternalCall: '本地配置或数据库查询',
    activeBinding: '仅使用中的绑定',
    semanticInputSource: '场景转场语义文本',
    playerInputSource: '玩家原始输入',
    similarityDescending: '按余弦相似度从高到低排序',
    semanticDecisionNext: '候选进入模型判定，不按向量排名直接选片',
    localVectorSearch: '本地向量索引查询，无外部调用日志',
    llmDecision: '模型判定事件一致性、剧透约束和画面连续性',
    intentLlmDecision: '模型判断候选是否符合玩家意图，可选择一条或全部拒绝',
    topOneThresholdNoLlm: '第一名达到直接命中线即选中；不调用模型',
    intentSelectionNext: '第一名未达到直接命中阈值，召回候选进入模型选片判定',
    belowMinimumNoLlm: '所有候选均低于候选有效线；不调用模型',
    noCandidateDecision: '未召回候选，不执行选片判定',
    playableStoryboards: '待审核或已发布资源，包含未生成视频的分镜',
    playableVideos: '待审核或已发布且已有视频的资源',
    intentStoryboards: '同一剧本和意图视频模板，包含未生成视频的分镜',
    intentVideos: '同一剧本和意图视频模板，且已有视频',
    exactResourceStore: '视频资源库',
    semanticBindingStore: '语义复用绑定',
    sceneVectorIndex: '场景视频向量索引',
    intentVectorIndex: '意图视频向量索引',
    queryCount: '{count} 条',
    bucketAvailable: '存在匹配分桶',
    facts: {
      queryTarget: '查询对象',
      queryResult: '查询结果',
      nextStep: '后续步骤',
      switchResult: '开关结果',
      settingResult: '玩家设置',
      scenarioVersion: '剧本版本',
      transitionHash: '精准转场指纹',
      resourceScope: '候选范围',
      externalCall: '调用类型',
      bindingStatus: '绑定状态',
      inputSource: '输入来源',
      rankingRule: '排序规则',
      nextDecision: '后续判定',
      decisionMethod: '选片规则',
      candidateLimit: '最多召回数量',
      minimumSimilarityThreshold: '候选有效线',
      directMatchThreshold: '直接命中线',
      eligibleCandidateCount: '送入模型的候选',
      selectedResource: '最终资源',
      finalSimilarity: '最终相似度',
    },
    summary: {
      cgRecallGate: {
        passed: '视频召回总开关开启，继续匹配。',
        stopped: '视频召回总开关关闭，终止匹配。',
      },
      smartMatchSetting: {
        passed: '视频演出开启，允许执行动作视频匹配。',
        stopped: '视频演出关闭，跳过动作视频匹配。',
      },
      exactMatch: {
        selected: '视频资源库返回 1 条，写入最终选片结果。',
        miss: '视频资源库返回 0 条。',
      },
      savedSemanticMatch: {
        selected: '历史语义绑定返回 1 条。',
        miss: '历史语义绑定返回 0 条。',
      },
      semanticCandidateBucket: {
        available: '场景视频向量索引存在匹配分桶，继续计算场景语义向量。',
        empty: '场景视频向量索引返回 0 条，跳过场景向量和模型判定，进入意图召回。',
      },
      semanticEmbedding: {
        ok: '场景转场语义文本向量计算成功。',
        error: '场景语义向量计算失败，终止场景召回。',
      },
      semanticVectorSearch: {
        ok: '召回 {count} 条场景候选，进入模型判定。',
        empty: '未召回场景候选，跳过模型判定。',
      },
      semanticDecision: {
        selected: '模型判定通过并选中候选。',
        rejected: '模型判定全部候选不满足约束。',
        error: '模型判定失败，未选中候选。',
      },
      intentSelection: {
        selected: '模型对照玩家输入后选中一条意图候选。',
        rejected: '模型判定全部意图候选不符合玩家输入。',
        error: '模型选片判定失败，未选中资源。',
      },
      intentEmbedding: {
        ok: '玩家原始输入向量计算成功。',
        error: '玩家原始输入向量计算失败，终止意图召回。',
      },
      intentVectorSearch: {
        selected: '相似度降序召回最多 {limit} 条。第一名 {similarity} ≥ {threshold}，选中资源 {resource}。本节点不调用模型。',
        needs_llm_selection: '相似度降序召回最多 {limit} 条。第一名 {similarity} 位于候选有效线 {minimum} 与直接命中线 {direct} 之间，将 {eligible} 条有效候选送入模型选片。',
        below_minimum: '相似度降序召回最多 {limit} 条。第一名 {similarity} < 候选有效线 {minimum}，不向模型发送候选。',
        empty: '意图索引未返回候选。本节点不调用模型。',
      },
      result: {
        selected: '已写入最终资源和匹配依据。',
        uncovered: '全部匹配层级均未选中资源。',
      },
    },
  },
  openRun: '查看本局完整链路',
  openFocusedRun: '在完整链路中定位',
  filters: {
    mode: '交互方式',
    intent: '归一化意图',
    versionId: '剧本版本',
    locationId: '场景位置',
    embeddingModelId: '向量模型',
    semanticTemplateVersion: '语义模板',
  },
}
